import { useEffect, useRef, useState, lazy, Suspense } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { Nav } from "./components/Nav";
import { Footer } from "./components/Footer";
import { SchemaJsonLd } from "./components/SchemaJsonLd";
import { RouteHead } from "./components/RouteHead";
import { MobileBottomBar } from "./components/MobileBottomBar";
import { BackToTop } from "./components/BackToTop";
import { WhatsAppFab } from "./components/WhatsAppFab";
import { RecentBookings } from "./components/RecentBookings";
import { Toast } from "./components/Toast";
import { business, DEMO_CONTENT } from "./data/business";
import Home from "./pages/Home";

// Route-level code-split — every non-Home page becomes its own chunk so
// the first paint downloads only what the landing visitor needs. Detail
// covers /rafting + /lodge + /bunkhouse + /camping + /sugarloaf + /bbq +
// /hikes + /events (8 routes that share the same component file), and
// Legal covers 8 policy routes. Splitting these out of the entry bundle
// is the biggest gzipped-JS win available without an asset pipeline.
const Detail = lazy(() => import("./pages/Detail"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const Legal = lazy(() => import("./pages/Legal"));
const ShopPage = lazy(() => import("./pages/ShopPage"));

// /groups, /weddings, /scouts removed — the original site only has a single
// "Events" page, and we don't host separate stub pages for each program.
const legalSlugs = [
  "waiver", "cancellation", "privacy", "terms", "accessibility",
  "faq", "what-to-bring", "safety",
];

// Polish round (nav + mobile + perf):
// Item #24 — opt out of the browser's default scroll restoration so we can
// implement our own. The default behaviour interacts badly with SPA route
// changes that swap content asynchronously (e.g. /gallery → back to /). We
// stash scrollY per pathname in sessionStorage and restore on back/forward.
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

export default function App() {
  const location = useLocation();
  // Item #16 — route transition fade. We flip an `is-loading` class on
  // <main> when the pathname changes and clear it on the next animation
  // frame so CSS can interpolate opacity 0 → 1. 150ms total, kept short
  // so navigation never feels sluggish on slow connections.
  const [isLoading, setIsLoading] = useState(false);
  const firstRenderRef = useRef(true);

  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }
    setIsLoading(true);
    const id = window.requestAnimationFrame(() => {
      // Two RAFs: first one paints the dimmed state, second one fades in.
      window.requestAnimationFrame(() => setIsLoading(false));
    });
    return () => window.cancelAnimationFrame(id);
  }, [location.pathname]);

  // Item #24 — scroll restoration on back/forward; fresh navigations
  // (PUSH) scroll to top, but POP restores the prior scroll offset for
  // the destination pathname so returning from /gallery doesn't lose
  // the visitor's place on /.
  useEffect(() => {
    const key = `scroll:${location.pathname}`;
    const navType =
      (window.performance?.getEntriesByType?.("navigation")[0] as
        | PerformanceNavigationTiming
        | undefined)?.type;

    // Save scroll position when leaving this path
    const saveScroll = () => {
      try { sessionStorage.setItem(key, String(window.scrollY)); } catch {}
    };
    window.addEventListener("pagehide", saveScroll);

    // Restore on back/forward only
    if (navType === "back_forward") {
      const stored = sessionStorage.getItem(key);
      if (stored !== null) {
        const y = Number(stored);
        if (!Number.isNaN(y)) {
          // Defer to after paint so the new route content has laid out
          requestAnimationFrame(() => window.scrollTo(0, y));
        }
      }
    }

    return () => {
      saveScroll();
      window.removeEventListener("pagehide", saveScroll);
    };
  }, [location.pathname]);

  return (
    <div className="page">
      <a href="#main" className="skip-link">Skip to main content</a>
      <SchemaJsonLd />
      <RouteHead />
      <Nav />
      <main id="main" className={isLoading ? "is-loading" : ""}>
        {/* Suspense fallback is intentionally minimal — the lazy chunks
            are small (each route is <50KB gzipped) and Nav's hover-
            prefetch hints have already warmed them by the time the user
            taps. An aria-busy attribute lets assistive tech announce
            the brief transition without painting a visible spinner. */}
        <Suspense
          fallback={
            <div
              className="route-suspense"
              aria-busy="true"
              aria-label="Loading page"
            />
          }
        >
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            {legalSlugs.map((s) => (
              <Route key={s} path={`/${s}`} element={<Legal slug={s} />} />
            ))}
            <Route path="/:slug" element={<Detail />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
      <BackToTop />
      {/* Item #54 — MobileBottomBar renders site-wide so FAQ / policy /
          shop pages all get the quick-action dock. Confirmed unconditional. */}
      <MobileBottomBar />
      {business.whatsappEnabled && <WhatsAppFab />}
      {/* Live "recent bookings" toast uses fictional names — gated until the
          booking provider can feed real (anonymised) recent activity. */}
      {DEMO_CONTENT && <RecentBookings />}
      {/* Generic toast — fires from anywhere via fireToast(message). Used
          to acknowledge actions that complete in a new tab (Book CTAs)
          or in a separate app (mailto: submit). */}
      <Toast />
    </div>
  );
}
